import { useState, useId, useCallback, useEffect } from 'react';
import {
  ShieldOff,
  ChevronDown,
  X,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Loader2,
  UserPlus,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { fetchPropertySettings, updatePropertySettings } from '@/services/api/admin.api';

// ─── Types ───────────────────────────────────────────────────────────────────

type SettingsTab = 'general' | 'users' | 'notifications' | 'integrations';

interface MockUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  joinedAt: string;
}

interface NotificationSetting {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
}

interface IntegrationField {
  id: string;
  label: string;
  value: string;
  isSensitive: boolean;
  readOnly?: boolean;
}

interface IntegrationSection {
  id: string;
  title: string;
  description: string;
  fields: IntegrationField[];
  hasTestButton?: boolean;
  status?: 'connected' | 'disconnected';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'users', label: 'Users' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'integrations', label: 'Integrations' },
];

const CURRENCIES = [
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'AED', label: 'AED - UAE Dirham' },
];

const ROLE_LABELS: Record<UserRole, string> = {
  // super_admin is never surfaced in the tenant-facing UI; neutral fallback only.
  [UserRole.SUPER_ADMIN]: 'Admin',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.REVENUE_MANAGER]: 'Revenue Manager',
  [UserRole.OPERATIONS]: 'Operations',
  [UserRole.OWNER]: 'Owner',
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Full access to all features',
  [UserRole.ADMIN]: 'Full access to all features',
  [UserRole.REVENUE_MANAGER]: 'Manage rates, channels, and reports',
  [UserRole.OPERATIONS]: 'Manage housekeeping and maintenance',
  [UserRole.OWNER]: 'Read-only access to their properties',
};

const ROLE_STYLES: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'bg-gray-100 text-gray-700',
  [UserRole.ADMIN]: 'bg-purple-100 text-purple-700',
  [UserRole.REVENUE_MANAGER]: 'bg-blue-100 text-blue-700',
  [UserRole.OPERATIONS]: 'bg-orange-100 text-orange-700',
  [UserRole.OWNER]: 'bg-green-100 text-green-700',
};

/** Roles shown to tenant users (excludes super_admin). */
const TENANT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.REVENUE_MANAGER,
  UserRole.OPERATIONS,
  UserRole.OWNER,
];

// ─── Mock Data ───────────────────────────────────────────────────────────────

const INITIAL_USERS: MockUser[] = [
  { id: 'u-1', name: 'Alice Nakamura', email: 'alice@bestacco.com', role: UserRole.ADMIN, isActive: true, joinedAt: '2024-01-15' },
  { id: 'u-2', name: 'Ben Carter', email: 'ben@bestacco.com', role: UserRole.REVENUE_MANAGER, isActive: true, joinedAt: '2024-03-02' },
  { id: 'u-3', name: 'Clara Santos', email: 'clara@bestacco.com', role: UserRole.OPERATIONS, isActive: true, joinedAt: '2024-03-10' },
  { id: 'u-4', name: 'David Okafor', email: 'david@bestacco.com', role: UserRole.OWNER, isActive: true, joinedAt: '2024-04-01' },
  { id: 'u-5', name: 'Elena Petrov', email: 'elena@bestacco.com', role: UserRole.OPERATIONS, isActive: false, joinedAt: '2024-05-20' },
  { id: 'u-6', name: 'Frank Muller', email: 'frank@bestacco.com', role: UserRole.OWNER, isActive: true, joinedAt: '2024-06-08' },
];

const INITIAL_NOTIFICATIONS: NotificationSetting[] = [
  { id: 'notif-booking', label: 'Booking confirmation email', description: 'Send email confirmation to guests when a booking is confirmed.', enabled: true },
  { id: 'notif-checkin', label: 'Check-in reminder', description: 'Send a reminder to guests 24 hours before check-in.', enabled: true },
  { id: 'notif-cancel', label: 'Cancellation notification', description: 'Notify property managers and guests when a booking is cancelled.', enabled: true },
  { id: 'notif-payout', label: 'Owner payout notification', description: 'Send monthly payout summary to property owners.', enabled: false },
  { id: 'notif-whatsapp', label: 'WhatsApp notifications', description: 'Send booking and check-in notifications via WhatsApp.', enabled: false },
];

const INITIAL_INTEGRATIONS: IntegrationSection[] = [
  {
    id: 'int-airbnb',
    title: 'Airbnb',
    description: 'Airbnb Channel API credentials',
    hasTestButton: true,
    status: 'connected',
    fields: [
      { id: 'airbnb-key', label: 'API Key', value: 'ak_live_7f3b2c9e1a4d8f6b', isSensitive: true },
    ],
  },
  {
    id: 'int-booking',
    title: 'Booking.com',
    description: 'Booking.com Connectivity API credentials',
    hasTestButton: true,
    status: 'connected',
    fields: [
      { id: 'booking-key', label: 'API Key', value: 'bk_prod_4e9c8d2f1a7b3e5a', isSensitive: true },
    ],
  },
  {
    id: 'int-expedia',
    title: 'Expedia',
    description: 'Expedia Partner Solutions API credentials',
    hasTestButton: true,
    status: 'disconnected',
    fields: [
      { id: 'expedia-key', label: 'API Key', value: 'ex_prod_2d6a4c8e9f1b3d7e', isSensitive: true },
    ],
  },
  {
    id: 'int-stripe',
    title: 'Stripe (Payment Gateway)',
    description: 'Stripe payment processing',
    fields: [
      { id: 'stripe-pk', label: 'Publishable Key', value: 'pk_live_51abc123def456', isSensitive: true },
      { id: 'stripe-webhook', label: 'Webhook URL', value: 'https://api.bestacco.com/webhooks/stripe', isSensitive: false, readOnly: true },
    ],
  },
  {
    id: 'int-sendgrid',
    title: 'SendGrid (Email)',
    description: 'Email delivery service',
    fields: [
      { id: 'sendgrid-key', label: 'API Key', value: 'SG.xyzABC123def456ghi789', isSensitive: true },
    ],
  },
  {
    id: 'int-twilio',
    title: 'Twilio (WhatsApp)',
    description: 'WhatsApp messaging via Twilio',
    fields: [
      { id: 'twilio-sid', label: 'Account SID', value: 'AC1234567890abcdef1234567890abcdef', isSensitive: true },
      { id: 'twilio-token', label: 'Auth Token', value: 'auth_9876543210fedcba9876543210fedcba', isSensitive: true },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function showToast(message: string) {
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.className = [
    'fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg',
    'bg-gray-900 text-white text-sm font-medium',
    'animate-[fadeIn_0.2s_ease-out]',
  ].join(' ');
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    el.style.transition = 'opacity 0.3s';
    setTimeout(() => el.remove(), 300);
  }, 2500);
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Access Denied ───────────────────────────────────────────────────────────

function AccessDeniedCard() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
        <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400 max-w-sm">
          Settings is restricted to Admins only. Contact your administrator for access.
        </p>
      </div>
    </div>
  );
}

// ─── Masked Field ────────────────────────────────────────────────────────────

function MaskedField({
  id,
  label,
  value,
  isSensitive,
  readOnly,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  isSensitive: boolean;
  readOnly?: boolean;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const fieldId = useId();

  return (
    <div>
      <label htmlFor={`${fieldId}-${id}`} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={`${fieldId}-${id}`}
          type={isSensitive && !visible ? 'password' : 'text'}
          value={isSensitive && !visible ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          className={cn(
            'block w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm text-gray-900 placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            readOnly && 'bg-gray-50 text-gray-500 cursor-default',
          )}
        />
        {isSensitive && (
          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
            aria-label={visible ? `Hide ${label}` : `Show ${label}`}
          >
            {visible ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── General Tab ─────────────────────────────────────────────────────────────

function GeneralTab() {
  const companyId = useId();
  const currencyId = useId();
  const checkInId = useId();
  const checkOutId = useId();
  const revenueShareId = useId();

  const propertyId = useSelectedPropertyId();

  const [companyName, setCompanyName] = useState('BestAcco Property Management');
  const [currency, setCurrency] = useState('USD');
  const [checkInTime, setCheckInTime] = useState('15:00');
  const [checkOutTime, setCheckOutTime] = useState('11:00');
  const [revenueShare, setRevenueShare] = useState('2');
  const [isSaving, setIsSaving] = useState(false);

  // Load currency from the selected property on mount / property change
  useEffect(() => {
    if (!propertyId) return;
    fetchPropertySettings(propertyId)
      .then((s) => {
        if (s.currency) setCurrency(s.currency);
        if (s.checkInTime) setCheckInTime(s.checkInTime);
        if (s.checkOutTime) setCheckOutTime(s.checkOutTime);
      })
      .catch(() => {});
  }, [propertyId]);

  async function handleSave() {
    setIsSaving(true);
    try {
      if (propertyId) {
        await updatePropertySettings(propertyId, {
          currency,
          checkInTime,
          checkOutTime,
        });
      }
      showToast('Settings saved successfully.');
    } catch {
      showToast('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-sm font-semibold text-gray-900 mb-5">General Settings</h3>

      <div className="space-y-5">
        {/* Company Name */}
        <div>
          <label htmlFor={companyId} className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            id={companyId}
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Default Currency */}
        <div>
          <label htmlFor={currencyId} className="block text-sm font-medium text-gray-700 mb-1">
            Default Currency
          </label>
          <div className="relative max-w-xs">
            <select
              id={currencyId}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Check-in / Check-out */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={checkInId} className="block text-sm font-medium text-gray-700 mb-1">
              Default Check-in Time
            </label>
            <input
              id={checkInId}
              type="time"
              value={checkInTime}
              onChange={(e) => setCheckInTime(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label htmlFor={checkOutId} className="block text-sm font-medium text-gray-700 mb-1">
              Default Check-out Time
            </label>
            <input
              id={checkOutId}
              type="time"
              value={checkOutTime}
              onChange={(e) => setCheckOutTime(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Revenue Share */}
        <div>
          <label htmlFor={revenueShareId} className="block text-sm font-medium text-gray-700 mb-1">
            Revenue Share Percentage
          </label>
          <div className="relative max-w-[120px]">
            <input
              id={revenueShareId}
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={revenueShare}
              onChange={(e) => setRevenueShare(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
              %
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-400">Platform commission deducted from owner payouts.</p>
        </div>
      </div>

      {/* Save */}
      <div className="mt-8 pt-5 border-t border-gray-100">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            isSaving ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
          )}
        >
          {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Users Tab ───────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<MockUser[]>(INITIAL_USERS);
  const [showInviteSlideOver, setShowInviteSlideOver] = useState(false);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);

  function confirmToggle(userId: string) {
    const user = users.find((u) => u.id === userId);
    if (user?.isActive) {
      setConfirmDeactivateId(userId);
    } else {
      // Reactivate immediately — no confirm needed
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: true } : u)));
      showToast('User reactivated.');
    }
  }

  function handleDeactivateConfirm() {
    if (!confirmDeactivateId) return;
    setUsers((prev) =>
      prev.map((u) => (u.id === confirmDeactivateId ? { ...u, isActive: false } : u)),
    );
    showToast('User deactivated.');
    setConfirmDeactivateId(null);
  }

  function handleInvite(data: { firstName: string; lastName: string; email: string; role: UserRole }) {
    const newUser: MockUser = {
      id: `u-${Date.now()}`,
      name: `${data.firstName} ${data.lastName}`.trim(),
      email: data.email,
      role: data.role,
      isActive: true,
      joinedAt: new Date().toISOString().split('T')[0],
    };
    setUsers((prev) => [...prev, newUser]);
    setShowInviteSlideOver(false);
    showToast(`Invitation sent to ${data.email}.`);
  }

  const confirmUser = users.find((u) => u.id === confirmDeactivateId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Team Members</h3>
          <p className="text-xs text-gray-500 mt-0.5">{users.filter((u) => u.isActive).length} active members</p>
        </div>
        <button
          type="button"
          onClick={() => setShowInviteSlideOver(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <UserPlus size={15} aria-hidden="true" />
          Invite User
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full" aria-label="Team members">
          <thead className="bg-gray-50">
            <tr>
              {['Member', 'Email', 'Role', 'Status', 'Joined', 'Actions'].map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center shrink-0',
                        user.isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-400',
                      )}
                      aria-hidden="true"
                    >
                      {getInitials(user.name)}
                    </div>
                    <span className={cn('text-sm font-medium', user.isActive ? 'text-gray-900' : 'text-gray-400')}>
                      {user.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Mail size={12} className="text-gray-400 shrink-0" aria-hidden="true" />
                    {user.email}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', ROLE_STYLES[user.role])}>
                    {ROLE_LABELS[user.role]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                      user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                    )}
                  >
                    <span
                      className={cn('w-1.5 h-1.5 rounded-full', user.isActive ? 'bg-green-500' : 'bg-gray-400')}
                      aria-hidden="true"
                    />
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                  {fmtDate(user.joinedAt)}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => confirmToggle(user.id)}
                    className={cn(
                      'text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                      user.isActive
                        ? 'border-red-200 text-red-600 hover:bg-red-50'
                        : 'border-green-200 text-green-600 hover:bg-green-50',
                    )}
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Slide-over */}
      {showInviteSlideOver && (
        <InviteUserSlideOver onClose={() => setShowInviteSlideOver(false)} onInvite={handleInvite} />
      )}

      {/* Deactivate Confirmation Dialog */}
      {confirmDeactivateId && confirmUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deactivate-dialog-title"
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-red-500" aria-hidden="true" />
              </div>
              <div>
                <h3 id="deactivate-dialog-title" className="text-sm font-semibold text-gray-900">
                  Deactivate {confirmUser.name}?
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  This user will lose access to the system. You can reactivate them at any time.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeactivateId(null)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-sm font-medium text-gray-700 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivateConfirm}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Invite User Slide-over ───────────────────────────────────────────────────

function InviteUserSlideOver({
  onClose,
  onInvite,
}: {
  onClose: () => void;
  onInvite: (data: { firstName: string; lastName: string; email: string; role: UserRole }) => void;
}) {
  const headingId = useId();
  const firstNameId = useId();
  const lastNameId = useId();
  const emailId = useId();
  const roleId = useId();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.OPERATIONS);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    onInvite({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), role });
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" aria-hidden="true" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h2 id={headingId} className="text-base font-semibold text-gray-900">Invite Team Member</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          <p className="text-sm text-gray-500">
            An invitation email will be sent. The user will be able to set their own password.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor={firstNameId} className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                id={firstNameId}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Alice"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor={lastNameId} className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                id={lastNameId}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Nakamura"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">
              Email <span aria-label="required">*</span>
            </label>
            <input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@company.com"
              required
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label htmlFor={roleId} className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <div className="relative">
              <select
                id={roleId}
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="block w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {TENANT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
            </div>
            {/* Role description */}
            <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
              {ROLE_DESCRIPTIONS[role]}
            </p>

            {/* Role descriptions reference */}
            <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 divide-y divide-gray-100">
              {TENANT_ROLES.map((r) => (
                <div key={r} className={cn('px-3 py-2 flex items-start gap-2', r === role && 'bg-primary-50 rounded-lg')}>
                  <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium shrink-0 mt-0.5', ROLE_STYLES[r])}>
                    {ROLE_LABELS[r]}
                  </span>
                  <p className="text-xs text-gray-500">{ROLE_DESCRIPTIONS[r]}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="inline-flex items-center gap-2 w-full justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <UserPlus size={15} aria-hidden="true" />
              Send Invitation
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

// ─── Notifications Tab ───────────────────────────────────────────────────────

function NotificationsTab() {
  const [notifications, setNotifications] = useState<NotificationSetting[]>(INITIAL_NOTIFICATIONS);

  const toggle = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, enabled: !n.enabled } : n)),
    );
  }, []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 max-w-2xl">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Notification Preferences</h3>
        <p className="text-xs text-gray-500 mt-0.5">Configure automated notifications for guests and staff.</p>
      </div>
      <ul className="divide-y divide-gray-100" role="list" aria-label="Notification settings">
        {notifications.map((notif) => {
          const toggleId = `toggle-${notif.id}`;
          return (
            <li key={notif.id} className="px-6 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <label htmlFor={toggleId} className="text-sm font-medium text-gray-900 cursor-pointer">
                  {notif.label}
                </label>
                <p className="text-xs text-gray-500 mt-0.5">{notif.description}</p>
              </div>
              <button
                id={toggleId}
                type="button"
                role="switch"
                aria-checked={notif.enabled}
                onClick={() => toggle(notif.id)}
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out cursor-pointer mt-0.5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                  notif.enabled ? 'bg-primary-600' : 'bg-gray-200',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out',
                    notif.enabled ? 'translate-x-5' : 'translate-x-0',
                  )}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Integrations Tab ────────────────────────────────────────────────────────

function IntegrationsTab() {
  const [integrations, setIntegrations] = useState<IntegrationSection[]>(INITIAL_INTEGRATIONS);
  const [testingId, setTestingId] = useState<string | null>(null);

  function handleFieldChange(sectionId: string, fieldId: string, value: string) {
    setIntegrations((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((f) =>
                f.id === fieldId ? { ...f, value } : f,
              ),
            }
          : section,
      ),
    );
  }

  function handleTestConnection(sectionId: string) {
    setTestingId(sectionId);
    setTimeout(() => {
      setTestingId(null);
      showToast('Connection test completed successfully.');
    }, 1500);
  }

  return (
    <div className="space-y-4 max-w-2xl">
      {integrations.map((section) => (
        <div key={section.id} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
            </div>
            {section.status && (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                  section.status === 'connected'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500',
                )}
              >
                {section.status === 'connected' ? (
                  <CheckCircle2 size={12} aria-hidden="true" />
                ) : (
                  <XCircle size={12} aria-hidden="true" />
                )}
                {section.status === 'connected' ? 'Connected' : 'Disconnected'}
              </span>
            )}
          </div>

          <div className="space-y-4">
            {section.fields.map((field) => (
              <MaskedField
                key={field.id}
                id={field.id}
                label={field.label}
                value={field.value}
                isSensitive={field.isSensitive}
                readOnly={field.readOnly ?? false}
                onChange={(v) => handleFieldChange(section.id, field.id, v)}
              />
            ))}
          </div>

          {section.hasTestButton && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => handleTestConnection(section.id)}
                disabled={testingId === section.id}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  testingId === section.id
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-primary-300 text-primary-600 hover:bg-primary-50',
                )}
              >
                {testingId === section.id ? (
                  <>
                    <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { user } = useAuth();
  const hasAccess = user?.role === UserRole.ADMIN;
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">System configuration, users, and integrations</p>
        </div>
      </div>

      {!hasAccess ? (
        <AccessDeniedCard />
      ) : (
        <>
          {/* Tab Bar — horizontal border-b style */}
          <nav aria-label="Settings tabs">
            <div className="border-b border-gray-200" role="tablist">
              <div className="flex gap-0">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    role="tab"
                    id={`settings-tab-${tab.id}`}
                    aria-selected={activeTab === tab.id}
                    aria-controls={`settings-panel-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                      activeTab === tab.id
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Tab Panels */}
          <div
            role="tabpanel"
            id={`settings-panel-${activeTab}`}
            aria-labelledby={`settings-tab-${activeTab}`}
          >
            {activeTab === 'general' && <GeneralTab />}
            {activeTab === 'users' && <UsersTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'integrations' && <IntegrationsTab />}
          </div>
        </>
      )}
    </div>
  );
}
